import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ChevronRight, ArrowLeft, Clock, AlertTriangle, Zap } from "lucide-react";
import { useOnboardingStore } from "@/stores/onboardingStore";

interface PriorityStepProps {
  initialImpact: number;
  initialUrgency: number;
  initialImpactMetric?: string;
  initialUrgencyReason?: string;
  initialTimeHorizonDays?: number | null;
  initialRiskToleranceLevel?: 'conservative' | 'moderate' | 'aggressive' | null;
  initialSuccessDefinition?: string;
  initialPrimaryMetric?: string;
  onNext: (impact: number, urgency: number, impactMetric: string, urgencyReason: string, extras: {
    timeHorizonDays: number | null;
    riskToleranceLevel: 'conservative' | 'moderate' | 'aggressive' | null;
    successDefinition: string;
    primaryMetric: string;
  }) => void;
  onBack: () => void;
}

const TIMELINE_OPTIONS = [
  { value: 2, label: "Wochen oder Monate", desc: "Kein akuter Zeitdruck", days: 180 },
  { value: 5, label: "Innerhalb von 30 Tagen", desc: "Bald, aber planbar", days: 30 },
  { value: 8, label: "Diese Woche", desc: "In den nächsten Tagen", days: 7 },
  { value: 10, label: "Sofort", desc: "Jetzt – keine Zeit verlieren", days: 1 },
];

const RISK_TOLERANCE_OPTIONS = [
  { value: 'conservative' as const, label: "Konservativ", desc: "Sicherheit geht vor – lieber vorsichtig" },
  { value: 'moderate' as const, label: "Moderat", desc: "Ausgewogenes Verhältnis von Risiko und Ertrag" },
  { value: 'aggressive' as const, label: "Offensiv", desc: "Bereit, für größere Chancen mehr Risiko einzugehen" },
];

/** Auto-compute impact from stakes data already collected */
function computeImpactFromStakes(store: {
  stakesWrongChoiceA: string;
  stakesWrongChoiceB: string;
  reversibilityA: string;
  reversibilityB: string;
  stakesWorstCaseProbability: string;
  stakesInaction: string;
}): { score: number; label: string } {
  let score = 0;

  // Text density → substance
  const textA = store.stakesWrongChoiceA?.length ?? 0;
  const textB = store.stakesWrongChoiceB?.length ?? 0;
  if (textA > 50 || textB > 50) score += 2;
  if (textA > 100 || textB > 100) score += 1;

  // Reversibility
  const revMap: Record<string, number> = { "irreversibel": 3, "schwer": 2, "mittel": 1, "schnell": 0 };
  const revA = Object.entries(revMap).find(([k]) => store.reversibilityA?.toLowerCase().includes(k))?.[1] ?? 0;
  const revB = Object.entries(revMap).find(([k]) => store.reversibilityB?.toLowerCase().includes(k))?.[1] ?? 0;
  score += Math.max(revA, revB);

  // Worst case probability
  if (store.stakesWorstCaseProbability?.toLowerCase().includes("hoch")) score += 2;
  else if (store.stakesWorstCaseProbability?.toLowerCase().includes("mittel")) score += 1;

  // Inaction cost
  if ((store.stakesInaction?.length ?? 0) > 30) score += 1;

  // Map to impact level
  if (score >= 7) return { score: 10, label: "Existenziell" };
  if (score >= 5) return { score: 8, label: "Hoher Einfluss" };
  if (score >= 3) return { score: 5, label: "Mittlerer Einfluss" };
  return { score: 2, label: "Geringer Einfluss" };
}

export const PriorityStep = ({ initialUrgency, initialUrgencyReason, initialTimeHorizonDays, initialRiskToleranceLevel, initialSuccessDefinition, initialPrimaryMetric, onNext, onBack }: PriorityStepProps) => {
  const store = useOnboardingStore();
  const [urgency, setUrgency] = useState(initialUrgency || 0);
  const [urgencyReason, setUrgencyReason] = useState(initialUrgencyReason || '');
  const [riskTolerance, setRiskTolerance] = useState<'conservative' | 'moderate' | 'aggressive' | null>(initialRiskToleranceLevel || null);
  const [successDefinition, setSuccessDefinition] = useState(initialSuccessDefinition || '');
  const [primaryMetric, setPrimaryMetric] = useState(initialPrimaryMetric || '');

  const computedImpact = useMemo(() => computeImpactFromStakes(store), [
    store.stakesWrongChoiceA, store.stakesWrongChoiceB,
    store.reversibilityA, store.reversibilityB,
    store.stakesWorstCaseProbability, store.stakesInaction,
  ]);

  const isValid = urgency > 0 && riskTolerance !== null;
  const showUrgencyReason = urgency >= 8;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Clock className="h-5 w-5" />
          </div>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Schritt 4 von 4</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold font-serif leading-tight text-foreground">
          Wie schnell braucht ihr Klarheit?
        </h2>
        <p className="text-muted-foreground text-base">
          Die strategische Tragweite berechnen wir aus euren bisherigen Angaben.
        </p>
      </div>

      {/* Auto-computed impact display */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-xl border transition-all",
        computedImpact.score >= 8
          ? "border-primary/40 bg-primary/5"
          : "border-border/40 bg-card/30"
      )}>
        <Zap className={cn("h-5 w-5 shrink-0", computedImpact.score >= 8 ? "text-primary" : "text-muted-foreground")} />
        <div>
          <div className="text-sm font-semibold text-foreground">
            Strategische Tragweite: {computedImpact.label}
          </div>
          <div className="text-xs text-muted-foreground">
            Automatisch berechnet aus Risiko, Reversibilität und Worst Case
          </div>
        </div>
      </div>

      {/* Timeline selection */}
      <div className="space-y-4">
        <h3 className="font-semibold text-base text-foreground">Wann braucht ihr eine Entscheidung?</h3>
        <div className="grid grid-cols-1 gap-2">
          {TIMELINE_OPTIONS.map((option) => {
            const isSelected = urgency === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setUrgency(option.value)}
                className={cn(
                  "group flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 text-left",
                  "bg-card/50 backdrop-blur-sm",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                    : "border-border/60 hover:border-primary/40 hover:bg-card"
                )}
              >
                <div className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all duration-300",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                )}>
                  <Clock className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "font-semibold text-sm transition-colors",
                    isSelected ? "text-foreground" : "text-foreground/90"
                  )}>{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        {showUrgencyReason && (
          <div className="pl-14 space-y-2 animate-fade-in">
            <label className="text-sm font-medium text-muted-foreground block">
              Warum ist das dringend?
            </label>
            <Input
              value={urgencyReason}
              onChange={(e) => setUrgencyReason(e.target.value)}
              placeholder="z.B. Deadline, Vertragsfrist, Marktfenster"
              className="rounded-xl border-border/60"
            />
            {!urgencyReason && (
              <p className="text-xs text-muted-foreground/70 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Ein Grund hilft uns, die Dringlichkeit besser einzuordnen.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Risk Tolerance */}
      <div className="space-y-4">
        <h3 className="font-semibold text-base text-foreground">Wie risikotolerant seid ihr bei dieser Entscheidung?</h3>
        <div className="grid grid-cols-1 gap-2">
          {RISK_TOLERANCE_OPTIONS.map((option) => {
            const isSelected = riskTolerance === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setRiskTolerance(option.value)}
                className={cn(
                  "group flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 text-left",
                  "bg-card/50 backdrop-blur-sm",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                    : "border-border/60 hover:border-primary/40 hover:bg-card"
                )}
              >
                <div className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all duration-300",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                )}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "font-semibold text-sm transition-colors",
                    isSelected ? "text-foreground" : "text-foreground/90"
                  )}>{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Success Definition (optional) */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground block">
          Woran messt ihr den Erfolg dieser Entscheidung? <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <Input
          value={successDefinition}
          onChange={(e) => setSuccessDefinition(e.target.value)}
          placeholder="z.B. 20% Umsatzsteigerung in 6 Monaten"
          className="rounded-xl border-border/60"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2 h-12 px-6 rounded-xl border-border/60 hover:bg-card hover:border-primary/40"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button
          onClick={() => {
            const selectedTimeline = TIMELINE_OPTIONS.find(o => o.value === urgency);
            onNext(computedImpact.score, urgency, '', urgencyReason, {
              timeHorizonDays: selectedTimeline?.days ?? null,
              riskToleranceLevel: riskTolerance,
              successDefinition,
              primaryMetric: primaryMetric || successDefinition,
            });
          }}
          disabled={!isValid}
          className="flex-1 gap-2 h-12 rounded-xl"
        >
          Analyse starten
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
